import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useEffectEvent } from 'academy-common-lib/hooks/useEffectEvent';
import UsageTracker from 'academy-common-lib/utils/UsageTracker';
import { Flex } from 'foundations-components/components/Flex';
import { FloatingAlertList, FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { DataFetchingClient, DataFetchingClientProvider, useDataFetchingClient } from 'data-fetching-client';
import { useChatManager } from 'breeze-engine-lib/chat/hooks/useChatManager';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import userInfo from 'hub-http/userInfo';
import { ChatHeader } from './ChatHeader';
import { ConversationHistoryDrawer } from './ConversationHistoryDrawer/ConversationHistoryDrawer';
import { ChatMainView } from './ChatMainView/ChatMainView';
import { ConversationListProvider, useConversationList } from '../context/ConversationListContext';
import { useDrawerPersistence } from '../hooks/useDrawerPersistence';
import { useConversationsList } from '../hooks/useConversationsList';
import { ABLY_AUTH_REGISTRY } from 'breeze-engine-lib/chat/ably/ablyToken';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ACADEMY_ASSISTANT_AGENT_ID = 'academy-assistant';
export const useHasConversations = () => {
  const {
    items,
    initialLoading
  } = useConversationsList(ACADEMY_ASSISTANT_AGENT_ID);
  return {
    hasConversations: items.length > 0,
    loading: initialLoading
  };
};
const dataFetchingClient = new DataFetchingClient();
const ChatSessionView = ({
  onClose,
  onMessagesChange,
  initialPrompt,
  conversationId: conversationIdProp,
  onConversationChange,
  agentId = ACADEMY_ASSISTANT_AGENT_ID,
  openHistoryOnMount,
  source
}) => {
  var _manager$active2;
  const {
    prepend,
    patch,
    refetch
  } = useConversationList();
  const userIdRef = useRef(0);
  const conversationIdRef = useRef(null);
  const messageCountRef = useRef(0);
  const hasContentClickRef = useRef(false);
  const messageSentAtRef = useRef(null);
  const firstTokenAtRef = useRef(null);
  const responseStoppedRef = useRef(false);
  const trackEvent = useCallback((action, extraProps) => {
    if (action === 'response-stopped') {
      responseStoppedRef.current = true;
    }
    UsageTracker.trackStandalone('Assistant Interaction', Object.assign({
      screen: 'academy-assistant'
    }, source !== undefined && {
      source
    }, {
      sessionId: conversationIdRef.current,
      action
    }, extraProps));
  }, [source]);
  const trackMessageSent = useCallback(appearsIn => {
    messageCountRef.current += 1;
    messageSentAtRef.current = Date.now();
    firstTokenAtRef.current = null;
    trackEvent('message-sent', {
      appearsIn,
      contentId: String(messageCountRef.current)
    });
  }, [trackEvent]);
  const onContentCardClick = useCallback((contentId, contentType) => {
    hasContentClickRef.current = true;
    trackEvent('content-link-clicked', {
      contentId: String(contentId),
      contentType
    });
  }, [trackEvent]);
  const onContentCardImpression = useCallback((contentId, contentType) => {
    trackEvent('content-link-impression', {
      contentId: String(contentId),
      contentType
    });
  }, [trackEvent]);
  const endSession = () => {
    if (messageCountRef.current > 0 && !hasContentClickRef.current) {
      trackEvent('no-content-found');
    }
    conversationIdRef.current = null;
    messageCountRef.current = 0;
    hasContentClickRef.current = false;
    messageSentAtRef.current = null;
    firstTokenAtRef.current = null;
    responseStoppedRef.current = false;
  };
  const handleEndSession = useEffectEvent(() => endSession());
  useEffect(() => {
    return () => handleEndSession();
  }, []);
  useEffect(() => {
    userInfo().then(({
      user
    }) => {
      userIdRef.current = user.user_id;
    }).catch(Raven.captureException.bind(Raven));
  }, []);
  const manager = useChatManager({
    agentId,
    mode: 'ably',
    onConversationCreated: id => {
      onConversationChange === null || onConversationChange === void 0 || onConversationChange(id);
      prepend({
        conversationId: id,
        agentId,
        status: 'ACTIVE',
        title: undefined,
        createdByUserId: userIdRef.current,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        collectionId: '',
        triggeredTask: false,
        unread: false
      });
    },
    onTitleChanged: (id, title) => {
      patch(id, {
        title
      });
    },
    onStatusChange: (_cId, status, prevStatus) => {
      const wasStreaming = prevStatus === 'STREAMING' || prevStatus === 'AWAITING_TOOL' || prevStatus === 'AWAITING_APPROVAL';
      if (wasStreaming && status === 'READY') {
        refetch();
        if (messageSentAtRef.current !== null && !responseStoppedRef.current) {
          trackEvent('response-received', Object.assign({
            contentId: String(messageCountRef.current),
            totalTime: String(Date.now() - messageSentAtRef.current)
          }, firstTokenAtRef.current !== null && {
            timeToReply: String(firstTokenAtRef.current - messageSentAtRef.current)
          }));
        }
        messageSentAtRef.current = null;
        firstTokenAtRef.current = null;
        responseStoppedRef.current = false;
      }
    },
    onStreamEvent: (_cId, event) => {
      var _raw$contentBlockDelt;
      if (firstTokenAtRef.current !== null || messageSentAtRef.current === null) {
        return;
      }
      const raw = event;
      if (raw.type === 'CONTENT_BLOCK_DELTA' && ((_raw$contentBlockDelt = raw.contentBlockDelta) === null || _raw$contentBlockDelt === void 0 ? void 0 : _raw$contentBlockDelt.type) === 'TEXT_DELTA' && raw.contentBlockDelta.delta) {
        firstTokenAtRef.current = Date.now();
      }
    },
    onError: (_cId, err) => {
      var _cause, _err$message;
      const cause = err && typeof err === 'object' && 'cause' in err ? err.cause : undefined;
      if (cause !== null && typeof cause === 'object' && 'cause' in cause && ((_cause = cause.cause) === null || _cause === void 0 ? void 0 : _cause.errorType) === 'NOT_FOUND') {
        endSession();
        manager.startNew();
        onConversationChange === null || onConversationChange === void 0 || onConversationChange(null);
        FloatingAlertStore.addAlert({
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academy-ai-assistant.errors.loadFailedTitle"
          }),
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academy-ai-assistant.errors.loadFailedBody"
          }),
          type: 'danger'
        });
        return;
      }
      Raven.captureException(cause instanceof Error ? cause : new Error((_err$message = err === null || err === void 0 ? void 0 : err.message) !== null && _err$message !== void 0 ? _err$message : 'Chat error'));
    },
    getAblyAuthToken: ABLY_AUTH_REGISTRY[agentId]
  });
  useEffect(() => {
    var _manager$activeConver;
    conversationIdRef.current = (_manager$activeConver = manager.activeConversationId) !== null && _manager$activeConver !== void 0 ? _manager$activeConver : null;
  }, [manager.activeConversationId]);
  const {
    setOpen: setDrawerOpen
  } = useDrawerPersistence();
  const messages = useMemo(() => {
    var _manager$active$messa, _manager$active;
    return (_manager$active$messa = (_manager$active = manager.active) === null || _manager$active === void 0 ? void 0 : _manager$active.messages) !== null && _manager$active$messa !== void 0 ? _manager$active$messa : [];
  }, [(_manager$active2 = manager.active) === null || _manager$active2 === void 0 ? void 0 : _manager$active2.messages]);
  const hasMessages = messages.length > 0;
  const initChat = useEffectEvent(() => {
    if (initialPrompt) {
      manager.startNew();
      trackMessageSent('initial-prompt');
      manager.sendMessage(initialPrompt).catch(Raven.captureException.bind(Raven));
      return;
    }
    if (conversationIdProp != null) {
      try {
        manager.open(conversationIdProp);
      } catch (_unused) {
        manager.startNew();
      }
    } else {
      manager.startNew();
    }
  });
  useEffect(() => {
    initChat();
  }, []);
  const fireMessagesChange = useEffectEvent(msgs => {
    onMessagesChange === null || onMessagesChange === void 0 || onMessagesChange(msgs);
  });
  useEffect(() => {
    fireMessagesChange(messages);
  }, [messages]);
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    blockSize: "100%",
    children: [/*#__PURE__*/_jsx(ChatHeader, {
      hasMessages: hasMessages,
      onClose: () => {
        endSession();
        onClose();
      },
      onNewChat: () => {
        endSession();
        manager.startNew();
        onConversationChange === null || onConversationChange === void 0 || onConversationChange(null);
      },
      onOpenChats: () => setDrawerOpen(true),
      trackEvent: trackEvent
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      flex: 1,
      overflow: "hidden",
      children: [/*#__PURE__*/_jsx(ConversationHistoryDrawer, {
        activeConversationId: manager.activeConversationId,
        defaultOpen: openHistoryOnMount,
        onOpenConversation: id => {
          endSession();
          manager.open(id);
          onConversationChange === null || onConversationChange === void 0 || onConversationChange(id);
        },
        onStartNew: () => {
          endSession();
          manager.startNew();
          onConversationChange === null || onConversationChange === void 0 || onConversationChange(null);
        },
        trackEvent: trackEvent
      }), /*#__PURE__*/_jsx(ChatMainView, {
        manager: manager,
        trackMessageSent: trackMessageSent,
        trackEvent: trackEvent,
        onContentCardClick: onContentCardClick,
        onContentCardImpression: onContentCardImpression,
        onStartNewChat: () => {
          endSession();
          manager.startNew();
          onConversationChange === null || onConversationChange === void 0 || onConversationChange(null);
        }
      })]
    })]
  });
};
const AcademyAIAssistantChatContainerInner = props => {
  var _props$agentId;
  return /*#__PURE__*/_jsx(ConversationListProvider, {
    agentId: (_props$agentId = props.agentId) !== null && _props$agentId !== void 0 ? _props$agentId : ACADEMY_ASSISTANT_AGENT_ID,
    children: /*#__PURE__*/_jsx(ChatSessionView, {
      onClose: props.onClose,
      onMessagesChange: props.onMessagesChange,
      initialPrompt: props.initialPrompt,
      conversationId: props.conversationId,
      onConversationChange: props.onConversationChange,
      agentId: props.agentId,
      openHistoryOnMount: props.openHistoryOnMount,
      source: props.source
    })
  });
};
export const AcademyAIAssistantChatContainer = props => {
  const existingClient = useDataFetchingClient(false);
  return /*#__PURE__*/_jsxs(DataFetchingClientProvider, {
    client: existingClient !== null && existingClient !== void 0 ? existingClient : dataFetchingClient,
    children: [/*#__PURE__*/_jsx(AcademyAIAssistantChatContainerInner, Object.assign({}, props)), /*#__PURE__*/_jsx(FloatingAlertList, {
      style: {
        zIndex: 10000
      }
    })]
  });
};